import { Component, OnInit, Input, DoCheck, EventEmitter, ChangeDetectorRef, ViewRef, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core';

import { FieldType } from "@ngx-formly/core";
import { Options } from "ngx-slider-v2";
import { AppStateService } from "src/app/core-module/services/app-state.service";
import { restore } from "./../../../../assets/json/restoreTooltip.en";
import { AloAocStateService } from "../../../core-module/services/state/alo-aoc-state.service";
import { DeviceConfigStateService } from "../../../core-module/services/state/device-config-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { SchemaDefaultComparatorService } from "../../../core-module/services/state/schema-default-comparator.service";
@Component({
  standalone: false,
  selector: "app-slider",
  templateUrl: "./slider.component.html",
  styleUrls: ["./slider.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent
  extends FieldType
  implements OnInit, DoCheck, OnDestroy
{
  @Input() field;
  valOptions: Options = {};
  floorValue;
  ceilValue;
  defaultValue;
  isResetDisabled;
  manualRefresh: EventEmitter<void> = new EventEmitter<void>();
  sliderValue;
  textboxValue;
  private readonly defaultColorSignal = signal(true);
  deviceName;
  private readonly isHiddenSignal = signal(false);
  private isHiddenRaw = false;
  isAocEnabled;

  enableRSet;

  aocSliderValue: boolean;
  quickConfigvalue;
  quickConfigCustomCSS;
  setQuickConfig;
  private readonly isExternalRsetSignal = signal(false);
  restoreText = restore;
  ngModelval: LegacyAny = {};
  properties: LegacyAny;
  inputError: LegacyAny = {};
  aocCreatFile;
  private readonly isHiddenEnableSignal = signal(false);
  private isHiddenEnableRaw = false;
  private readonly isEnableSignal = signal(false);
  private readonly isAocDisableSignal = signal(false);

  get defaultColor(): boolean { return this.defaultColorSignal(); }
  set defaultColor(value: boolean) { this.defaultColorSignal.set(!!value); }

  get isHidden(): boolean { return this.isHiddenRaw; }
  set isHidden(value: boolean) {
    this.isHiddenRaw = value;
    this.isHiddenSignal.set(!!value);
  }

  get isExternalRset(): boolean { return this.isExternalRsetSignal(); }
  set isExternalRset(value: boolean) { this.isExternalRsetSignal.set(!!value); }

  get isHiddenEnable(): boolean { return this.isHiddenEnableRaw; }
  set isHiddenEnable(value: boolean) {
    this.isHiddenEnableRaw = value;
    this.isHiddenEnableSignal.set(!!value);
  }

  get isEnable(): boolean { return this.isEnableSignal(); }
  set isEnable(value: boolean) { this.isEnableSignal.set(!!value); }

  get isAocDisable(): boolean { return this.isAocDisableSignal(); }
  set isAocDisable(value: boolean) { this.isAocDisableSignal.set(!!value); }

  constructor(
    private ref: ChangeDetectorRef, private appState: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private aloAoc: AloAocStateService,
    private readonly deviceConfig: DeviceConfigStateService,
    private readonly schemaDefaults: SchemaDefaultComparatorService
  ) {
    super();
  }

  ngOnInit() {
    this.setupSubscriptions();

    if (!this.field || !this.field.properties || !this.field.properties.AocCurrent) {
      this.initializeSafeDefaults();
      return;
    }

    this.initializeSliderProperties();
    this.initializeAocEnabledState();

    const isConfigEdited = this.processUnselectedFeatures();
    if (!isConfigEdited) {
      this.processFeatureData();
    }

    this.subscribeToQuickConfig();
    this.finalizeInitialization();
  }

  private setupSubscriptions(): void {
    this.featureDefaults.getAocDefaultValue.subscribe((response) => {
      if (response) {
        this.resetSlider();
      }
    });
    this.aloAoc.aocSliderCick.subscribe((data) => {
      this.aocSliderValue = data["sliderClick"];
    });
  }

  private initializeSafeDefaults(): void {
    this.valOptions = { floor: 0, ceil: 100 };
    this.textboxValue = 0;
    this.ngModelval["AocCurrent"] = 0;
  }

  private initializeSliderProperties(): void {
    this.properties = this.field.properties;
    this.defaultValue = this.field.properties.AocCurrent.default;
    this.floorValue = this.field.properties.AocCurrent.minimum;
    this.ceilValue = this.field.properties.AocCurrent.maximum;
    this.valOptions = {
      floor: this.floorValue,
      ceil: this.ceilValue,
    };
    this.ngModelval["AocCurrent"] = this.textboxValue = this.field.properties.AocCurrent.default;
  }

  private initializeAocEnabledState(): void {
    const aocEnabled = this.field?.properties?.AocEnabled;

    // Some schemas expose only AocCurrent and have no AocEnabled metadata.
    // In this case, keep slider editable and rely on AocCurrent-only default checks.
    if (!aocEnabled) {
      delete this.ngModelval["AocEnabled"];
      this.isExternalRset = false;
      this.isEnable = true;
      this.isAocDisable = false;
      return;
    }

    const label = aocEnabled.aocEnabledLabel;

    // Schemas that mark AocEnabled as `hidden` (or `readOnly`) have no user
    // toggle in the UI — the AOC feature is always considered enabled for
    // that device. Force the slider editable and treat AocEnabled as true.
    if (this.isAocEnabledSchemaHidden()) {
      this.ngModelval["AocEnabled"] = true;
      this.isAocDisable = false;
      return;
    }

    if (label === 0 || label === "0" || label === "1" || label === 1) {
      if (label === 1 || label === "1") {
        this.isHidden = true;
        this.ngModelval["AocEnabled"] = aocEnabled.default;
        this.isAocDisable = this.isExternalRset = !aocEnabled.default;
      } else {
        this.isHiddenEnable = true;
        this.isAocDisable = !aocEnabled.default;
        this.ngModelval["AocEnabled"] = aocEnabled.default;
      }
    } else {
      this.isAocDisable = !aocEnabled.const;
    }
  }

  /**
   * True when the schema hides the AocEnabled control from the user
   * (either via an explicit `hidden` flag or `readOnly: true`). In that
   * case there is no UI toggle to flip, so the slider must not depend on
   * whatever value the device happens to report for AocEnabled.
   */
  private isAocEnabledSchemaHidden(): boolean {
    const aocEnabled = this.field?.properties?.AocEnabled;
    if (!aocEnabled) {
      return false;
    }
    return aocEnabled.hidden === true || aocEnabled.readOnly === true;
  }

  private processUnselectedFeatures(): boolean {
    const configAocData = this.getConfiguredAocData();
    if (this.shouldPreferConfigDataForAoc() && configAocData?.AocCurrent !== undefined) {
      return false;
    }

    const unSelectedFeat = this.appState.getUnSelectedFeatConfig();
    if (!unSelectedFeat) {
      return false;
    }

    let isConfigEdited = false;
    let featIndex = 0;

    unSelectedFeat.forEach((element) => {
      if (element["featureName"] === "AOC") {
        isConfigEdited = true;
        this.ngModelval["AocCurrent"] = this.textboxValue = element["featureParameters"][0]["AocCurrent"];
        this.applyAocEnabledFromData(element["featureParameters"][0]["AocEnabled"]);
        unSelectedFeat.splice(featIndex, 1);
        this.appState.setUnSelectedFeatConfig(unSelectedFeat);
      } else {
        featIndex++;
      }
    });

    return isConfigEdited;
  }

  private processFeatureData(): void {
    const configAocData = this.getConfiguredAocData();
    const shouldPreferConfigData = this.shouldPreferConfigDataForAoc();

    const aocData = this.normalizeAocPayload(
      shouldPreferConfigData
        ? (configAocData || this.appState.getFeatureData('AOC'))
        : this.appState.getFeatureData('AOC')
    );
    if (!aocData) {
      return;
    }

    // Reflect the device's value in the model regardless of range — the
    // enable-state logic below is what actually allows or blocks user edits.
    this.ngModelval["AocCurrent"] = this.textboxValue = aocData["AocCurrent"];
    this.applyAocEnabledFromData(aocData["AocEnabled"]);
  }

  private getConfiguredAocData(): LegacyAny {
    const resp = this.appState.getConfigurationData()?.resp;
    if (!resp) {
      return null;
    }

    // Read-flow merge can leave stale/cased variants of AOC keys.
    // Resolve by scanning keys case-insensitively and preferring lowercase
    // variants first when multiple candidates exist.
    const keys = Object.keys(resp);
    const normalizedMatches = keys.filter((key) => key?.trim()?.toLowerCase() === "aoc");
    if (!normalizedMatches.length) {
      return null;
    }

    const rankedCandidates = normalizedMatches
      .map((key) => ({
        key,
        payload: this.normalizeAocPayload(resp[key]),
        rank: this.getAocKeyPriority(key),
      }))
      .filter((candidate) => !!candidate.payload)
      .sort((left, right) => left.rank - right.rank);

    if (!rankedCandidates.length) {
      return null;
    }

    return rankedCandidates[0].payload;
  }

  private getAocKeyPriority(key: string): number {
    if (key === "aoc") {
      return 0;
    }

    // Mixed-case keys often come from newer read merges and should win over
    // stale legacy uppercase values.
    if (key !== "AOC") {
      return 1;
    }

    return 2;
  }

  private normalizeAocPayload(rawPayload: LegacyAny): LegacyAny {
    if (!rawPayload) {
      return null;
    }

    if (rawPayload["AocCurrent"] !== undefined) {
      return rawPayload;
    }

    const featureParameters = rawPayload["featureParameters"];
    if (Array.isArray(featureParameters)) {
      const firstValid = featureParameters.find((entry) => entry && entry["AocCurrent"] !== undefined);
      if (firstValid) {
        return firstValid;
      }
    }

    if (featureParameters && featureParameters["AocCurrent"] !== undefined) {
      return featureParameters;
    }

    return null;
  }

  private shouldPreferConfigDataForAoc(): boolean {
    const hasAocEnabledSchema = !!this.field?.properties?.AocEnabled;

    // For AocCurrent-only schemas, always trust ConfigurationData first.
    // This avoids stale SaveSetData/UnSelectedFeatConfig from a previous
    // device/session causing false "Restore default" states.
    return this.appState.getReadingDevice() || !hasAocEnabledSchema;
  }

  private applyAocEnabledFromData(aocEnabledValue: LegacyAny): void {
    const aocEnabled = this.field?.properties?.AocEnabled;

    // Schemas without AocEnabled should evaluate default-state from AocCurrent only.
    if (!aocEnabled) {
      delete this.ngModelval["AocEnabled"];
      this.isExternalRset = false;
      this.isEnable = true;
      this.isAocDisable = false;
      return;
    }

    // When the schema hides the AocEnabled toggle the user has no way to
    // re-enable AOC from the UI, so any value the device reports for
    // AocEnabled must not disable the slider. Keep it editable and coerce
    // the model to `true` so downstream writes/exports stay consistent.
    if (this.isAocEnabledSchemaHidden()) {
      this.isExternalRset = false;
      this.isEnable = true;
      this.isAocDisable = false;
      this.ngModelval["AocEnabled"] = true;
      return;
    }

    if (!this.isAocEnabledFieldVisible()) {
      this.isExternalRset = false;
      this.isEnable = false;
      this.isAocDisable = false;
      delete this.ngModelval["AocEnabled"];
      return;
    }

    const hasAocEnabledValue = aocEnabledValue !== undefined && aocEnabledValue !== null;
    const defaultAocEnabled = !!this.schemaDefaults.getSchemaDefault(aocEnabled);

    if (aocEnabled.aocEnabledLabel === 1) {
      this.isExternalRset = aocEnabledValue ? false : !defaultAocEnabled;
      this.isEnable = false;
      this.ngModelval["AocEnabled"] = !this.isExternalRset;
      this.rSetChanged(this.isExternalRset);
    } else {
      this.isExternalRset = false;
      this.isEnable = hasAocEnabledValue ? !!aocEnabledValue : defaultAocEnabled;
      this.ngModelval["AocEnabled"] = this.isEnable;
      this.enableChanged(this.isEnable);
    }

    // The user just landed on the AOC field with data already populated
    // from a device read / imported feature file / restored save-set. Even
    // if the raw value happens to serialise as `AocEnabled: false`, the
    // presence of an `AocCurrent` in `ngModelval` means there is a value
    // to edit — keep the slider input editable so the user can adjust it
    // without needing to "Restore default" first.
    // Only override the disabled state when External Rset is NOT active;
    // when External Rset is checked the input must remain disabled.
    if (this.ngModelval["AocCurrent"] !== undefined && this.ngModelval["AocCurrent"] !== null && !this.isExternalRset) {
      this.isAocDisable = false;
    }
  }

  private isAocEnabledFieldVisible(): boolean {
    return this.isHidden === false ||
           this.isHidden === true ||
           this.isHiddenEnable === false ||
           this.isHiddenEnable === true;
  }

  private subscribeToQuickConfig(): void {
    this.aloAoc.getquickConfig.subscribe((data) => {
      if (data === "quickConfig") {
        this.quickConfigvalue = true;
        this.quickConfigCustomCSS = "quick-custom-inputbox textBoxUnit";
        this.setQuickConfig = "quickConfig";
      } else if (data === "standard") {
        this.quickConfigvalue = false;
        this.quickConfigCustomCSS = "custom-inputbox";
        this.setQuickConfig = "standard";
      }
      setTimeout(() => {
        if (this.ref && !(this.ref as ViewRef).destroyed) {
          this.ref.detectChanges();
        }
      });
    });
  }

  private finalizeInitialization(): void {
    this.checkDefault();
    this.aloAoc.aocValueChange(this.ngModelval);
    this.checkValidValue();
  }
  ngDoCheck() {
    if (this.aocSliderValue) {
      this.manualRefresh.emit();
    }
  }

  checkDefault() {
    const ignoredKeys: string[] = [];
    if (!this.properties?.AocEnabled || this.isAocEnabledSchemaHidden()) {
      ignoredKeys.push("AocEnabled");
    }

    this.defaultColor = this.schemaDefaults.isModelAtSchemaDefaults(
      this.ngModelval,
      this.properties,
      {
        ignoreKeys: ignoredKeys,
        keyTransform: (key: string) => key.replace("Val", ""),
        compareOnlyModelKeys: true,
      }
    );

    this.featureDefaults.setAocDefaultColor(this.defaultColor);
  }
  sliderEvent(val) {}

  aocValuechange(value) {
    this.inputError["AocCurrent"] = false;
    if (value >= this.floorValue && this.inputError["AocCurrent"] === false) {
      this.ngModelval["AocCurrent"] = value;
      this.textboxValue = value;
      this.checkDefault();
      this.setAocConfigurationData(this.ngModelval);
      this.aloAoc.aocValueChange(this.ngModelval);
      this.deviceConfig.setFormInputValidation(false);
    }
  }

  checkValidValue() {
    let value = parseInt(this.ngModelval["AocCurrent"]);
    if (isNaN(value)) {
      value = 0;
    }
    if (value < this.floorValue || value > this.ceilValue) {
      this.inputError["AocCurrent"] = true;
    } else {
      this.inputError["AocCurrent"] = false;
    }

    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.aocCreatFile = true;
      }
    }
    this.deviceConfig.setFormInputValidation(this.aocCreatFile);
  }
  enableAocSlider($event) {
    this.isAocEnabled = !this.isAocEnabled;
  }
  valueChanged(value) {
    value = parseInt(value);
    this.defaultColor = false;
    this.aocCreatFile = false;

    if (isNaN(value)) {
      // `null` renders the input as empty; coercing to 0 refilled the box
      // when the user deleted the last digit.
      this.inputError["AocCurrent"] = true;
      this.ngModelval["AocCurrent"] = null;
    } else if (value < this.floorValue || value > this.ceilValue) {
      this.inputError["AocCurrent"] = true;
      this.ngModelval["AocCurrent"] = value;
    } else {
      this.inputError["AocCurrent"] = false;
      this.textboxValue = value;
      this.ngModelval["AocCurrent"] = value;
    }

    this.checkDefault();
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.aocCreatFile = true;
      }
    }
    this.featureDefaults.setAocDefaultColor(this.defaultColor);
    this.deviceConfig.setFormInputValidation(this.aocCreatFile);
    this.setAocConfigurationData(this.ngModelval);
    this.aloAoc.aocValueChange(this.ngModelval);
  }

  resetSlider() {
    this.ngModelval["AocCurrent"] = this.textboxValue =
      this.field.properties.AocCurrent.default;
    this.inputError["AocCurrent"] = false;
    const aocEnabled = this.field?.properties?.AocEnabled;
    if (!aocEnabled) {
      delete this.ngModelval["AocEnabled"];
      this.isExternalRset = false;
      this.isEnable = true;
      this.isAocDisable = false;
      this.defaultColor = true;
      this.setAocConfigurationData(this.ngModelval);
      this.aloAoc.aocValueChange(this.ngModelval);
      this.featureDefaults.setAocDefaultColor(this.defaultColor);
      this.deviceConfig.setFormInputValidation(false);
      this.ref.markForCheck();
      return;
    }
    if (
      aocEnabled.aocEnabledLabel === 0 ||
      aocEnabled.aocEnabledLabel === "0" ||
      aocEnabled.aocEnabledLabel === "1" ||
      aocEnabled.aocEnabledLabel === 1
    ) {
      if (
        aocEnabled.aocEnabledLabel === "1" ||
        aocEnabled.aocEnabledLabel === 1
      ) {
        this.ngModelval["AocEnabled"] =
          aocEnabled.default;
        this.isExternalRset = !this.ngModelval["AocEnabled"];
        this.isAocDisable = !aocEnabled.default;
      } else {
        this.ngModelval["AocEnabled"] =
          aocEnabled.default;
        this.isEnable = this.ngModelval["AocEnabled"];
        this.isAocDisable = !aocEnabled.default;
      }
    } else {
      this.isAocDisable = !aocEnabled.const;
    }

    this.defaultColor = true;
    this.setAocConfigurationData(this.ngModelval);
    this.aloAoc.aocValueChange(this.ngModelval);
    this.featureDefaults.setAocDefaultColor(this.defaultColor);
    this.deviceConfig.setFormInputValidation(false);
    this.ref.markForCheck();
  }

  rSetChanged(checked) {
    this.enableRSet = checked;
    this.isExternalRset = checked;
    this.isAocDisable = checked;
    this.ngModelval["AocEnabled"] = !checked;
    if (this.enableRSet) {
      this.ngModelval["AocEnabled"] = !this.enableRSet;
    }
    this.checkDefault();
    this.setAocConfigurationData(this.ngModelval);
    this.aloAoc.aocValueChange(this.ngModelval);
    this.featureDefaults.setAocDefaultColor(this.defaultColor);
  }

  enableChanged(checked) {
    this.enableRSet = checked;
    this.isEnable = checked;
    this.isAocDisable = !checked;
    this.ngModelval["AocEnabled"] = checked;
    if (this.enableRSet) {
      this.ngModelval["AocEnabled"] = checked;
    }
    this.checkDefault();
    this.setAocConfigurationData(this.ngModelval);
    this.aloAoc.aocValueChange(this.ngModelval);
    this.featureDefaults.setAocDefaultColor(this.defaultColor);
  }

  numberOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  // Use centralized method to save AOC feature data
  // This automatically saves to both ConfigurationData and SaveSetData
  setAocConfigurationData(value) {
    this.appState.setFeatureData('AOC', value);
  }

  ngOnDestroy() {
    this.aloAoc.aocValueChange("");
    this.textboxValue = null;
    this.sliderValue = null;
    this.defaultValue = null;
  }
}

